import React from "react";

import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatListBulleted from "@mui/icons-material/FormatListBulleted";
import FormatListNumbered from "@mui/icons-material/FormatListNumbered";
import FormatTextdirectionLToROutlinedIcon from "@mui/icons-material/FormatTextdirectionLToROutlined";
import FormatTextdirectionRToLOutlinedIcon from "@mui/icons-material/FormatTextdirectionRToLOutlined";
import { useEditorState } from "@tiptap/react";
import {
  MenuButton,
  MenuButtonBold,
  MenuButtonEditLink,
  MenuButtonItalic,
  MenuButtonRemoveFormatting,
  MenuButtonUnderline,
  MenuControlsContainer,
  MenuSelectHeading,
  MenuSelectTextAlign,
  useRichTextEditorContext
} from "mui-tiptap";

import { useContentContext } from "./content-context";

const menuControlsContainerSx = {
  // background: "white !important",
  // Counteract parent menuBarContent padding (4px 0)
  margin: "-4px 0",
  // padding: "10px 0",

  ".MuiSelect-select": {
    padding: "2px 20px 2px 6px !important",
    color: "#071f35"
  },

  "& .MuiButtonBase-root": {
    color: "#071f35"
  },

  "& .MuiButtonBase-root svg": {
    color: "#071f35",
    fontSize: "18px"
  }
};

export const TextSectionToolbar: React.FC = () => {
  const editor = useRichTextEditorContext();
  const { currentSectionId, setCurrentSectionIsRtl } = useContentContext();

  // Force re-render when editor state changes (cursor move, selection, etc.)
  useEditorState({
    editor,
    selector: (snapshot) => snapshot.transactionNumber
  });

  const handleSetTextDirection = (direction: "ltr" | "rtl") => {
    if (currentSectionId && editor) {
      // Update UI state — the textDirection prop on RichTextEditor handles the visual change
      setCurrentSectionIsRtl(direction === "rtl");
      // Persist to database (single API call)
      editor.chain().focus().setTextDirection(direction).run();
      // updateSection({ id: currentSectionId, is_rtl: direction === "rtl" });
    }
  };

  return (
    <MenuControlsContainer sx={menuControlsContainerSx}>
      <MenuButtonBold sx={{ fill: "#071f35" }} />
      <MenuButtonItalic sx={{ fill: "#071f35" }} />
      <MenuButtonUnderline sx={{ fill: "#071f35" }} />
      <MenuSelectHeading sx={{ fill: "#071f35" }} />
      <MenuSelectTextAlign
        sx={{
          ".MuiSvgIcon-root": {
            fill: "#071f35 !important",
            fontSize: "18px"
          }
        }}
        emptyLabel={
          <FormatAlignLeftIcon
            sx={{ width: "16px", height: "16px", fill: "#071f35", verticalAlign: "middle", paddingTop: "2.5px" }}
          />
        }
      />
      <MenuButton
        tooltipLabel="Ordered list"
        tooltipShortcutKeys={["mod", "Shift", "7"]}
        IconComponent={FormatListNumbered}
        selected={editor?.isActive("orderedList") ?? false}
        disabled={!editor?.isEditable}
        onClick={() => editor?.chain().focus().toggleOrderedList().run()}
        sx={{ fill: "#071f35" }}
      />
      <MenuButton
        tooltipLabel="Bulleted list"
        tooltipShortcutKeys={["mod", "Shift", "8"]}
        IconComponent={FormatListBulleted}
        selected={editor?.isActive("bulletList") ?? false}
        disabled={!editor?.isEditable}
        onClick={() => editor?.chain().focus().toggleBulletList().run()}
        sx={{ fill: "#071f35" }}
      />
      <MenuButtonEditLink />
      <MenuButton
        tooltipLabel="Left to right"
        IconComponent={FormatTextdirectionLToROutlinedIcon}
        selected={editor?.isActive({ dir: "ltr" }) ?? false}
        disabled={!editor?.isEditable || !editor.can().setTextDirection("ltr")}
        onClick={() => handleSetTextDirection("ltr")}
        sx={{ fill: "#071f35" }}
      />
      <MenuButton
        tooltipLabel="Right to left"
        IconComponent={FormatTextdirectionRToLOutlinedIcon}
        selected={editor?.isEditable && (editor?.isActive({ dir: "rtl" }) ?? false)}
        disabled={!editor?.isEditable || !editor.can().setTextDirection("rtl")}
        onClick={() => handleSetTextDirection("rtl")}
        sx={{ fill: "#071f35" }}
      />
      <MenuButtonRemoveFormatting sx={{ fill: "#071f35" }} />
    </MenuControlsContainer>
  );
};
